import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";

/**
 * Error component for links
 * Displays an error message and retry button when link loading fails
 *
 * How it works:
 * 1. Renders a centered error state with an icon, message, and retry button
 * 2. Uses internationalization (i18n) for translating error messages
 * 3. Accepts an onRetry callback to handle retry attempts
 * 4. Uses destructive color scheme to indicate error state
 *
 * Key features:
 * - Localized text through i18n
 * - Clear visual indication of error with appropriate icon
 * - Interactive retry button for error recovery
 * - Consistent styling with other error states
 * - Responsive layout that works at different screen sizes
 *
 * Context:
 * This component is displayed by LinkList when there's an error
 * loading links. It follows the same pattern as
 * MediaAttachmentError and FileAttachmentError for consistency.
 */
interface LinkErrorProps {
  onRetry: () => void;
}

export default function LinkError({ onRetry }: LinkErrorProps) {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col items-center justify-center gap-3 p-4">
      <AlertCircle className="size-8 text-destructive" />
      <p className="text-center text-sm text-muted-foreground">
        {t("error_loading_links", "Error loading links")}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("retry", "Retry")}
      </Button>
    </div>
  );
}
